import { useEffect, useState } from 'react';
import AtlasGem from '../components/AtlasGem';
import AtlasCore from '../components/AtlasCore';
import { renderAtlasShareCard } from '../lib/shareCard';
import { loadAtlasEvents } from '../lib/atlasData';
import { useGameStore } from '../store/gameStore';
import { useAuth } from '../hooks/useAuth';
import {
  REGIONS, computeAtlas, knowledgeBand, knowledgeFraction,
  type AnswerEvent, type Atlas, type Difficulty, type RegionEstimate,
} from '../lib/cognitiveEngine';
import { rampColor } from '../lib/atlasGemModel';
import { MatchDeltaCard, RankCard, RatingGraph } from '../components/AtlasRank';

/**
 * Dev-only preview for the Atlas stone. Every visual change to AtlasGem gets
 * checked here first, in the states real players actually hit, before it is
 * looked at in the app. The empty state is the first one on purpose: it is what
 * every new account sees, and it is the state that broke the previous three
 * attempts at this visual.
 *
 * Not routed in production builds.
 */

const R = (region: string, name: string, elo: number, n: number, tier: RegionEstimate['tier']): RegionEstimate =>
  ({ region, name, elo, n, tier });

const NAMES: Array<[string, string]> = [
  ['SCIENCES', 'Sciences'],
  ['HUMANITIES', 'Humanities'],
  ['CULTURE', 'Culture'],
  ['LIFESTYLE', 'Lifestyle'],
  ['ABSTRACT', 'Abstract'],
];

const CASES: Array<{ label: string; regions: RegionEstimate[] }> = [
  {
    label: 'Brand new account, nothing charted',
    regions: NAMES.map(([k, n]) => R(k, n, 1000, 0, 'HIDDEN')),
  },
  {
    label: 'One region just charted, rest hidden',
    regions: NAMES.map(([k, n], i) => (i === 0 ? R(k, n, 1080, 12, 'FAINT') : R(k, n, 1000, 3, 'HIDDEN'))),
  },
  {
    label: 'Half charted, uneven',
    regions: NAMES.map(([k, n], i) =>
      i < 3 ? R(k, n, [1240, 1010, 900][i], 40, 'CHARTED') : R(k, n, 1000, 6, 'HIDDEN')
    ),
  },
  {
    label: 'Fully charted, one dominant region',
    regions: NAMES.map(([k, n], i) => R(k, n, [1300, 1020, 980, 940, 1060][i], 45, 'CHARTED')),
  },
  {
    label: 'Fully charted, flat and strong',
    regions: NAMES.map(([k, n]) => R(k, n, 1270, 60, 'CHARTED')),
  },
  {
    label: 'Fully charted, all weak',
    regions: NAMES.map(([k, n]) => R(k, n, 850, 50, 'CHARTED')),
  },
];

/**
 * Veteran profiles: 5000 answers each, all five regions charted, but with
 * different shapes. Same code, different data. These exist to prove the stone is
 * computed rather than decorative, and to check the top of the ramp, which the
 * small synthetic cases above never reach.
 *
 * Answer counts are split roughly by how many realms each region covers
 * (Sciences has three, Abstract has one), so the totals look like real play.
 */
const VETERANS: Array<{ label: string; elos: number[]; counts: number[] }> = [
  {
    label: 'The specialist. Deep in Humanities and Culture, thin on Lifestyle.',
    elos: [1180, 1310, 1265, 940, 1090],
    counts: [1500, 1150, 1100, 850, 400],
  },
  {
    label: 'The scientist. One commanding region, the rest merely solid.',
    elos: [1355, 1045, 990, 1020, 1210],
    counts: [2100, 900, 800, 700, 500],
  },
  {
    label: 'The generalist. Wide and even, nothing left uncharted.',
    elos: [1195, 1230, 1175, 1215, 1160],
    counts: [1400, 1000, 950, 950, 700],
  },
];

function VeteranCard({ v }: { v: (typeof VETERANS)[number] }) {
  const regions = NAMES.map(([k, n], i) => R(k, n, v.elos[i], v.counts[i], 'CHARTED'));
  const total = v.counts.reduce((s, c) => s + c, 0);
  return (
    <div style={{ width: 250 }}>
      <AtlasGem regions={regions} size={228} />
      <p style={{ fontSize: '0.82rem', color: '#c4c4cc', textAlign: 'center', marginTop: 12 }}>{v.label}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
        {regions.map(r => (
          <div key={r.region} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
              <span style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', color: '#b4b4b4' }}>
                {r.name.toUpperCase()}
              </span>
              <span style={{ color: '#e6e6e6' }}>{knowledgeBand(r.elo)}</span>
            </div>
            <div style={{ height: 5, background: '#2C3A58', borderRadius: 3 }}>
              <div style={{
                width: `${Math.max(3, knowledgeFraction(r.elo) * 100)}%`,
                height: '100%', borderRadius: '3px 4px 4px 3px',
                background: rampColor(knowledgeFraction(r.elo)),
              }} />
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', letterSpacing: '0.12em', color: '#bebec6', textAlign: 'center', marginTop: 10 }}>
        {total.toLocaleString()} ANSWERS
      </p>
    </div>
  );
}


// ── The Core: synthetic answer streams ──────────────────────────────
// Full event streams, not summaries: the disc consumes raw AnswerEvents, so the
// lab has to synthesize play histories. Seeded LCG, fully deterministic: the
// same profile renders the identical fingerprint on every load.

function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
}

interface PlaySpec {
  /** Share of play per region, canonical order. Sums to ~1. */
  weights: number[];
  /** Accuracy per region, canonical order. */
  acc: number[];
}

const REGION_KEYS = Object.keys(REGIONS);

function synthEvents(n: number, seed: number, spec: PlaySpec): AnswerEvent[] {
  const rnd = lcg(seed);
  const out: AnswerEvent[] = [];
  for (let i = 0; i < n; i++) {
    let pick = rnd();
    let region = 0;
    for (let k = 0; k < 5; k++) {
      pick -= spec.weights[k];
      if (pick <= 0) { region = k; break; }
      region = k;
    }
    const realms = REGIONS[REGION_KEYS[region]].realms;
    const dRoll = rnd();
    const difficulty: Difficulty = dRoll < 0.3 ? 'easy' : dRoll < 0.75 ? 'medium' : 'hard';
    const accAdj = difficulty === 'easy' ? 0.08 : difficulty === 'hard' ? -0.09 : 0;
    out.push({
      realm: realms[Math.floor(rnd() * realms.length)],
      difficulty,
      correct: rnd() < Math.max(0.05, Math.min(0.97, spec.acc[region] + accAdj)),
      responseTimeMs: 1800 + Math.floor(rnd() * 6500),
      roundIndex: (i % 10) + 1,
    });
  }
  return out;
}

const CORE_CASES: Array<{ label: string; sub: string; events: AnswerEvent[] }> = [
  {
    label: 'Yeni hesap',
    sub: '0 cevap. Çerçeve çizili, alan boş: davet, arıza değil.',
    events: [],
  },
  {
    label: 'İlk gece',
    sub: '15 cevap. Çekirdek tutmaya başlamış.',
    events: synthEvents(15, 11, { weights: [0.25, 0.25, 0.2, 0.2, 0.1], acc: [0.5, 0.55, 0.5, 0.45, 0.5] }),
  },
  {
    label: 'Isınıyor',
    sub: '300 cevap. İlk halkalar okunuyor.',
    events: synthEvents(300, 22, { weights: [0.3, 0.25, 0.2, 0.15, 0.1], acc: [0.6, 0.55, 0.5, 0.45, 0.5] }),
  },
  {
    label: 'Uzman, 5.000 cevap',
    sub: 'Humanities ve Culture yoğun ve parlak, Lifestyle seyrek.',
    events: synthEvents(5000, 33, { weights: [0.2, 0.34, 0.27, 0.12, 0.07], acc: [0.62, 0.8, 0.74, 0.45, 0.55] }),
  },
  {
    label: 'Bilim insanı, 5.000 cevap',
    sub: 'Sciences sektörü yanıyor, iri taneler orada.',
    events: synthEvents(5000, 44, { weights: [0.45, 0.14, 0.12, 0.09, 0.2], acc: [0.85, 0.55, 0.5, 0.5, 0.7] }),
  },
  {
    label: 'Zorlanan, 5.000 cevap',
    sub: 'Aynı kütle, daha az ışık. Emek görünür, sonuç da.',
    events: synthEvents(5000, 55, { weights: [0.2, 0.2, 0.2, 0.2, 0.2], acc: [0.35, 0.35, 0.35, 0.35, 0.35] }),
  },
];

function coreStats(events: AnswerEvent[]): string {
  if (events.length === 0) return '0 tanecik';
  const lit = events.filter(e => e.correct && !e.timedOut).length;
  return `${events.length.toLocaleString('tr-TR')} tanecik · yüzde ${Math.round((lit / events.length) * 100)} ışık`;
}


/** The player's own rank, from the same pipeline Profile uses. */
function RealRank() {
  const nickname = useGameStore(s => s.nickname);
  const { user, displayName } = useAuth();
  const [atlas, setAtlas] = useState<Atlas | null>(null);
  const displayNick = nickname
    ? nickname.toUpperCase()
    : displayName ? displayName.split(' ')[0].toUpperCase() : 'PLAYER';

  useEffect(() => {
    let live = true;
    loadAtlasEvents(user ? displayNick : null)
      .then(ev => { if (live) setAtlas(computeAtlas(ev, new Date())); })
      .catch(() => { /* stays hidden */ });
    return () => { live = false; };
  }, [user, displayNick]);

  if (!atlas) return null;
  return <RankCard atlas={atlas} label={`ATLAS RANK · ${displayNick} · GERÇEK`} />;
}

/** The player's own disc, from the same pipeline Profile uses. */
function RealCore() {
  const nickname = useGameStore(s => s.nickname);
  const { user, displayName } = useAuth();
  const [events, setEvents] = useState<AnswerEvent[] | null>(null);
  const displayNick = nickname
    ? nickname.toUpperCase()
    : displayName ? displayName.split(' ')[0].toUpperCase() : 'PLAYER';

  useEffect(() => {
    let live = true;
    loadAtlasEvents(user ? displayNick : null)
      .then(ev => { if (live) setEvents(ev); })
      .catch(() => { if (live) setEvents([]); });
    return () => { live = false; };
  }, [user, displayNick]);

  if (!events || events.length === 0) return null;
  return (
    <div style={{ width: 300 }}>
      <AtlasCore events={events} size={280} />
      <p style={{ fontSize: '0.85rem', color: '#e6e6e6', textAlign: 'center', marginTop: 10, fontWeight: 600 }}>
        GERÇEK VERİ · {displayNick}
      </p>
      <p style={{ fontSize: '0.8rem', color: '#ccccd4', textAlign: 'center', marginTop: 4 }}>
        {coreStats(events)}. Bu desen senin oyunundan başka hiçbir kaynaktan üretilemez.
      </p>
    </div>
  );
}

/** The share card renders from the same atlas the app does; checked here too. */
function ShareCardPreview({ regions }: { regions: RegionEstimate[] }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    const visible = regions.filter(r => r.tier !== 'HIDDEN').map(r => r.elo);
    const atlas: Atlas = {
      regions,
      globalElo: visible.length > 0 ? Math.round(visible.sort((a, b) => a - b)[Math.floor(visible.length / 2)]) : null,
      pace: null, steadiness: null, resilience: null,
      totalAnswers: regions.reduce((s, r) => s + r.n, 0),
    };
    renderAtlasShareCard({
      nickname: 'KEMAL', atlas,
      archetype: { key: 'LIGHTHOUSE', name: 'THE LIGHTHOUSE', line: 'One region shines far brighter than the rest.', leaning: false },
    }).then(b => { url = URL.createObjectURL(b); setSrc(url); });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [regions]);
  return src ? <img src={src} alt="share card" style={{ width: 320, border: '1px solid #33415E' }} /> : null;
}

export default function GemLab() {
  return (
    <div style={{ background: '#000', minHeight: '100vh', padding: 24, color: '#e6e6e6' }}>
      <h1 style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', letterSpacing: '0.2em', color: '#c6c6ce' }}>
        ATLAS GEM LAB
      </h1>

      {(() => {
        const uzman = CORE_CASES[3].events;
        const uzmanAtlas = computeAtlas(uzman, new Date());
        return (
          <>
            <h2 style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', letterSpacing: '0.2em', color: '#c6c6ce', marginTop: 24 }}>
              ATLAS RANK · TASARIM
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#ccccd4', maxWidth: 560, marginTop: 8 }}>
              Araştırmanın söylediği: oyuncuya söyleyecek bir cümle ver. Bir isim, bir sayı,
              maç başına bir delta, bir sonraki basamak. Merdiven: SCOUT, VOYAGER, NAVIGATOR,
              PIONEER, LUMINARY. Sayılar mock değil, motorun kendisinden.
            </p>
            {/* One grid, one card width, every edge aligned. The previous
                layout mixed three widths and left a black hole under the
                shorter column. */}
            <div style={{ display: 'grid', gridTemplateColumns: '360px 360px', gap: 24, marginTop: 20, alignItems: 'start' }}>
              <RealRank />
              <RankCard atlas={uzmanAtlas} label="ATLAS RANK · UZMAN · SENTETİK" />
              <MatchDeltaCard events={uzman} lastN={10} />
              <RatingGraph events={uzman} />
            </div>
          </>
        );
      })()}

      <h2 style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', letterSpacing: '0.2em', color: '#c6c6ce', marginTop: 48 }}>
        ÇEKİRDEK · HER CEVAP BİR TANECİK
      </h2>
      <p style={{ fontSize: '0.85rem', color: '#ccccd4', maxWidth: 560, marginTop: 8 }}>
        Özet yok. Beş bölge, sarmal galaksinin beş kolu. Her cevap kendi bölgesinin kolunda
        sıradaki yerini alır: kol oynadıkça uzar, doğru cevaplar yıldız olarak yanar, zor
        soruların doğruları turkuaz, yanlışlar ışığı örten toz. Yerleşim deterministik,
        aynı geçmiş her zaman aynı galaksiyi üretir. Tıkla, geçmiş kendini yeniden sersin.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 36, marginTop: 20 }}>
        <RealCore />
        {CORE_CASES.map(c => (
          <div key={c.label} style={{ width: 300 }}>
            <AtlasCore events={c.events} size={280} />
            <p style={{ fontSize: '0.85rem', color: '#e6e6e6', textAlign: 'center', marginTop: 10, fontWeight: 600 }}>
              {c.label.toUpperCase()}
            </p>
            <p style={{ fontSize: '0.8rem', color: '#ccccd4', textAlign: 'center', marginTop: 4 }}>
              {coreStats(c.events)}. {c.sub}
            </p>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginTop: 24 }}>
        {CASES.map(c => (
          <div key={c.label} style={{ width: 240 }}>
            <AtlasGem regions={c.regions} size={208} />
            <p style={{ fontSize: '0.82rem', color: '#ccccd4', textAlign: 'center', marginTop: 10 }}>{c.label}</p>
          </div>
        ))}
      </div>
      <h2 style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', letterSpacing: '0.2em', color: '#c6c6ce', marginTop: 48 }}>
        VETERANS &middot; 5,000 ANSWERS
      </h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, marginTop: 20 }}>
        {VETERANS.map(v => <VeteranCard key={v.label} v={v} />)}
      </div>

      <h2 style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', letterSpacing: '0.2em', color: '#c6c6ce', marginTop: 48 }}>
        SHARE CARD
      </h2>
      <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
        <ShareCardPreview regions={CASES[0].regions} />
        <ShareCardPreview regions={CASES[3].regions} />
      </div>
    </div>
  );
}
